import { Effect, Option, Schema as S } from "effect";
import * as Command from "foldkit/command";
import * as Dom from "foldkit/dom";
import type { Html } from "foldkit/html";
import { defineMessageUnion } from "foldkit/message";
import { defineTaggedUnion } from "foldkit/schema";
import { evo } from "foldkit/struct";
import { defineView } from "foldkit/submodel";
import type * as Update from "foldkit/update";
import { namedInteractions } from "../app/namedInteractions";

import { ChatHistoryMessage } from "../api/schemas/ai";
import { Route, linkHref } from "../app/routes";
import type { Api, Shared } from "../pages/shared";
import { busyAttributes, busySpinner } from "./busy";
import * as Icons from "./icons";

/**
 * v1 `AIChat.tsx` as a Submodel nested under the Chat page (KTD2's one
 * sanctioned sub-page nesting). The `/chat/stream` body is a Subscription
 * (`aiChatStream.ts`) gated on `pendingRequest`; this module only folds the
 * Messages it emits.
 */

// MODEL

export const RecordLinkItem = S.Struct({
	id: S.String,
	title: S.String,
	type: S.Literals(["node", "knot"]),
	url: S.String,
});
export type RecordLinkItem = typeof RecordLinkItem.Type;

export const Entry = defineTaggedUnion({
	Text: { id: S.String, content: S.String, sender: S.Literals(["user", "ai"]) },
	Tool: {
		id: S.String,
		toolName: S.String,
		toolCallId: S.Option(S.String),
		input: S.Option(S.Unknown),
		output: S.Option(S.Unknown),
		expanded: S.Boolean,
	},
	RecordLinks: { id: S.String, items: S.Array(RecordLinkItem) },
});
export type Entry = typeof Entry.Type;

export const PendingRequest = S.Struct({
	id: S.String,
	text: S.String,
	history: S.Array(ChatHistoryMessage),
});
export type PendingRequest = typeof PendingRequest.Type;

export const Model = S.Struct({
	input: S.String,
	entries: S.Array(Entry),
	pendingRequest: S.Option(PendingRequest),
	/** Ids are `u-<seq>` / `ai-<seq>` (v1 used `Date.now()`). */
	seq: S.Number,
	/** The tail of the last chunk that may be the start of a marker split across chunks. */
	buffer: S.String,
});
export type Model = typeof Model.Type;

export const init = (): Model => ({
	input: "",
	entries: [],
	pendingRequest: Option.none(),
	seq: 0,
	buffer: "",
});

// MESSAGE

export const Message = defineMessageUnion({
	ChangedInput: { value: S.String },
	Submitted: {},
	ChunkReceived: { id: S.String, text: S.String },
	StreamEnded: { id: S.String },
	StreamFailed: { id: S.String, error: S.String },
	ToggledToolLog: { id: S.String },
	CompletedFocus: {},
});
export type Message = typeof Message.Type;

// COMMAND

export const INPUT_ID = "ai-chat-input";

/** v1 focuses the textarea on mount and again after each stream settles. */
export const FocusInput = Command.define("FocusChatInput", {
	messages: [Message.CompletedFocus],
	execute: Dom.focus(`#${INPUT_ID}`).pipe(
		Effect.as(Message.CompletedFocus()),
		Effect.catch(() => Effect.succeed(Message.CompletedFocus())),
	),
});

// PARSING (v1 AIChat.tsx marker handling, as pure functions)

const TOOL_MARKER =
	/(\[Using tool: [^\]]+\]|<TOOL_LOG>.*?<\/TOOL_LOG>|<NODE_LINKS>.*?<\/NODE_LINKS>)/gs;
const TOOL_CALL = /\[Using tool: ([^|\]]+)(?:\s*\|\s*id:\s*([^\]]+))?\]/;
const TOOL_LOG = /<TOOL_LOG>(.*?)<\/TOOL_LOG>/s;
const NODE_LINKS = /<NODE_LINKS>(.*?)<\/NODE_LINKS>/s;

const parseJson = (source: string | undefined): unknown => {
	if (!source) return null;
	try {
		return JSON.parse(source);
	} catch {
		return null;
	}
};

const nonEmpty = (value: unknown): Option.Option<string> =>
	typeof value === "string" && value.trim() !== ""
		? Option.some(value.trim())
		: Option.none();

const isReply = (entry: Entry, id: string): entry is Entry & { _tag: "Text" } =>
	entry._tag === "Text" && entry.id === id;

/** Leading whitespace is dropped only when the reply is still empty or the "..." placeholder. */
const appendText = (
	entries: ReadonlyArray<Entry>,
	id: string,
	chunk: string,
): ReadonlyArray<Entry> =>
	chunk === ""
		? entries
		: entries.map((entry) => {
				if (!isReply(entry, id)) return entry;
				const fresh = entry.content === "..." || entry.content === "";
				return {
					...entry,
					content: fresh
						? chunk.replace(/^\s+/, "")
						: `${entry.content}${chunk}`,
				};
			});

const insertBefore = (
	entries: ReadonlyArray<Entry>,
	id: string,
	entry: Entry,
): ReadonlyArray<Entry> => {
	const at = entries.findIndex((e) => isReply(e, id));
	return at === -1
		? [...entries, entry]
		: [...entries.slice(0, at), entry, ...entries.slice(at)];
};

const structuralCount = (entries: ReadonlyArray<Entry>): number =>
	entries.filter((e) => e._tag !== "Text").length;

const insertTool = (
	entries: ReadonlyArray<Entry>,
	id: string,
	toolName: string,
	toolCallId: Option.Option<string>,
): ReadonlyArray<Entry> =>
	insertBefore(entries, id, {
		_tag: "Tool",
		id: `${id}-tool-${structuralCount(entries)}`,
		toolName,
		toolCallId,
		input: Option.none(),
		output: Option.none(),
		expanded: false,
	});

const attachLog = (
	entries: ReadonlyArray<Entry>,
	id: string,
	log: Record<string, unknown>,
): ReadonlyArray<Entry> => {
	const toolName = nonEmpty(log.toolName);
	const toolCallId = nonEmpty(log.toolCallId);
	const matches = (entry: Entry & { _tag: "Tool" }) =>
		Option.isSome(toolCallId)
			? Option.contains(entry.toolCallId, toolCallId.value)
			: Option.contains(toolName, entry.toolName);
	const known = entries.some((e) => e._tag === "Tool" && matches(e));
	const withTool =
		known || Option.isNone(toolName)
			? entries
			: insertTool(entries, id, toolName.value, toolCallId);
	return withTool.map((entry) =>
		entry._tag !== "Tool" || !matches(entry)
			? entry
			: {
					...entry,
					toolCallId: Option.orElse(entry.toolCallId, () => toolCallId),
					input:
						log.type === "call" && log.input !== undefined
							? Option.some(log.input)
							: entry.input,
					output:
						log.type === "result" && log.output !== undefined
							? Option.some(log.output)
							: entry.output,
				},
	);
};

const foldMarker = (
	entries: ReadonlyArray<Entry>,
	id: string,
	marker: string,
): ReadonlyArray<Entry> => {
	if (marker.startsWith("[Using tool:")) {
		const match = marker.match(TOOL_CALL);
		return insertTool(
			entries,
			id,
			match?.[1]?.trim() ?? "tool",
			nonEmpty(match?.[2]),
		);
	}
	if (marker.startsWith("<TOOL_LOG>")) {
		const log = parseJson(marker.match(TOOL_LOG)?.[1]);
		return typeof log === "object" && log !== null
			? attachLog(entries, id, log as Record<string, unknown>)
			: entries;
	}
	const items = parseJson(marker.match(NODE_LINKS)?.[1]);
	return Array.isArray(items) && items.length > 0
		? insertBefore(entries, id, {
				_tag: "RecordLinks",
				id: `${id}-links-${structuralCount(entries)}`,
				items: items as ReadonlyArray<RecordLinkItem>,
			})
		: entries;
};

/** Folds one stream chunk into the transcript: text appends to the reply `id`; markers become structural entries before it. */
export const foldChunk = (
	entries: ReadonlyArray<Entry>,
	id: string,
	chunk: string,
): ReadonlyArray<Entry> => {
	let result = entries;
	let last = 0;
	for (const match of chunk.matchAll(TOOL_MARKER)) {
		const at = match.index ?? 0;
		result = appendText(result, id, chunk.slice(last, at));
		result = foldMarker(result, id, match[0]);
		last = at + match[0].length;
	}
	return appendText(result, id, chunk.slice(last));
};

const OPENERS = ["[Using tool:", "<TOOL_LOG>", "<NODE_LINKS>"];
const closerOf = (opener: string) =>
	opener === "[Using tool:" ? "]" : opener.replace("<", "</");

/**
 * Splits `text` into the part safe to fold now and a tail that may be an
 * unfinished marker (an opener without its closer, or a prefix of one):
 * a network body can split `[Using tool: search]` across chunks.
 */
export const splitHeld = (text: string): readonly [string, string] => {
	for (let i = text.length - 1; i >= 0; i--) {
		if (text[i] !== "[" && text[i] !== "<") continue;
		const tail = text.slice(i);
		const opener = OPENERS.find(
			(o) => tail.startsWith(o) || o.startsWith(tail),
		);
		if (opener === undefined) continue;
		if (tail.startsWith(opener) && tail.includes(closerOf(opener))) break;
		return [text.slice(0, i), tail];
	}
	return [text, ""];
};

/** Display-time cleanup: stray or partial markers removed, 3+ newlines collapsed. */
export const sanitize = (content: string): string =>
	content
		.replace(/\[Using tool: [^\]]*\]?/g, "")
		.replace(/<TOOL_LOG>.*?<\/TOOL_LOG>/gs, "")
		.replace(/<NODE_LINKS>.*?<\/NODE_LINKS>/gs, "")
		.replace(/<TOOL_LOG>.*$/s, "")
		.replace(/<NODE_LINKS>.*$/s, "")
		.replace(/\n{3,}/g, "\n\n")
		.trim();

/** What the backend receives as prior turns: text and tool summaries, never record links. */
export const toHistory = (
	entries: ReadonlyArray<Entry>,
): ReadonlyArray<ChatHistoryMessage> =>
	entries.flatMap((entry) =>
		Entry.match<ReadonlyArray<ChatHistoryMessage>>(entry, {
			Text: ({ content, sender }) => [
				{ role: sender === "user" ? "user" : "assistant", content },
			],
			Tool: ({ toolName }) => [
				{ role: "assistant", content: `Using tool: ${toolName}` },
			],
			RecordLinks: () => [],
		}),
	);

// UPDATE

type Return = Update.Return<Model, Message, Api>;

const ERROR_PREFIX = "Sorry, I encountered an error. Please try again.";

const isPending = (model: Model, id: string): boolean =>
	Option.exists(model.pendingRequest, (request) => request.id === id);

export const update = (model: Model, message: Message): Return =>
	Message.match<Return>(message, {
		ChangedInput: ({ value }) => ({
			model: evo(model, { input: () => value }),
		}),
		Submitted: () => {
			const text = model.input.trim();
			if (text === "" || Option.isSome(model.pendingRequest)) {
				return { model };
			}
			const id = `ai-${model.seq}`;
			return {
				model: evo(model, {
					input: () => "",
					entries: (entries) => [
						...entries,
						{
							_tag: "Text",
							id: `u-${model.seq}`,
							content: text,
							sender: "user",
						},
						{ _tag: "Text", id, content: "...", sender: "ai" },
					],
					pendingRequest: () =>
						Option.some({ id, text, history: toHistory(model.entries) }),
					seq: (seq) => seq + 1,
					buffer: () => "",
				}),
			};
		},
		ChunkReceived: ({ id, text }) => {
			if (!isPending(model, id)) return { model };
			const [ready, held] = splitHeld(model.buffer + text);
			return {
				model: evo(model, {
					buffer: () => held,
					entries: (entries) => foldChunk(entries, id, ready),
				}),
			};
		},
		StreamEnded: ({ id }) =>
			isPending(model, id)
				? {
						model: evo(flushed(model, id), {
							pendingRequest: () => Option.none(),
						}),
						commands: [FocusInput()],
					}
				: { model },
		StreamFailed: ({ id, error }) =>
			isPending(model, id)
				? {
						model: evo(flushed(model, id), {
							pendingRequest: () => Option.none(),
							entries: (entries) =>
								entries.map((entry) => {
									if (!isReply(entry, id)) return entry;
									const partial = entry.content === "..." ? "" : entry.content;
									return {
										...entry,
										content: `${partial === "" ? "" : `${partial}\n\n`}${ERROR_PREFIX} (${error})`,
									};
								}),
						}),
						commands: [FocusInput()],
					}
				: { model },
		ToggledToolLog: ({ id }) => ({
			model: evo(model, {
				entries: (entries) =>
					entries.map((entry) =>
						entry._tag === "Tool" && entry.id === id
							? { ...entry, expanded: !entry.expanded }
							: entry,
					),
			}),
		}),
		CompletedFocus: () => ({ model }),
	});

/** Whatever was held back at the end of the stream is plain text after all. */
const flushed = (model: Model, id: string): Model =>
	evo(model, {
		buffer: () => "",
		entries: (entries) => foldChunk(entries, id, model.buffer),
	});

// SUBSCRIPTION SELECTOR

/** `Some` while a request is pending (the stream opens), `None` once it ended or failed (KTD12). */
export const streamDependencies = (model: Model) => ({
	maybeRequest: model.pendingRequest,
});

// VIEW

type Part =
	| { type: "text"; value: string }
	| { type: "link"; value: string; href: string };

/** v1 `parseMarkdownLinks`: `[label](https://…)` becomes a link part. */
export const parseMarkdownLinks = (content: string): ReadonlyArray<Part> => {
	const parts: Array<Part> = [];
	let last = 0;
	for (const match of content.matchAll(
		/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
	)) {
		const index = match.index ?? 0;
		if (index > last)
			parts.push({ type: "text", value: content.slice(last, index) });
		parts.push({ type: "link", value: match[1] ?? "", href: match[2] ?? "" });
		last = index + match[0].length;
	}
	if (last < content.length)
		parts.push({ type: "text", value: content.slice(last) });
	return parts;
};

const formatLog = (value: unknown): string => {
	try {
		return typeof value === "string" ? value : JSON.stringify(value, null, 2);
	} catch {
		return String(value);
	}
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const pending = Option.isSome(model.pendingRequest);
	const avatar = (classes: string, child: Html) =>
		h.div(
			[h.Class("avatar avatar-placeholder")],
			[h.div([h.Class(`w-10 rounded-full ${classes}`)], [child])],
		);
	const link = (href: string, label: string) =>
		h.a(
			[
				h.Href(href),
				h.Class("link link-primary break-words"),
				h.Target("_blank"),
				h.DataAttribute("analytics-event", namedInteractions.chatLink),
				h.Rel("noreferrer"),
			],
			[label],
		);
	/** v1 `renderTextWithUrls`: bare URLs become links, newlines become `<br>`. */
	const textWithUrls = (text: string): ReadonlyArray<Html> =>
		text.split("\n").flatMap((line, lineIndex, lines) => {
			const records: Array<Html> = [];
			let last = 0;
			for (const match of line.matchAll(/(https?:\/\/[^\s]+)/g)) {
				const start = match.index ?? 0;
				const url = match[1] ?? "";
				if (start > last) records.push(h.span([], [line.slice(last, start)]));
				records.push(link(url, url));
				last = start + url.length;
			}
			if (last < line.length) records.push(h.span([], [line.slice(last)]));
			if (lineIndex < lines.length - 1) records.push(h.br([]));
			return records;
		});
	/** v1 `renderMessageContent`. */
	const content = (raw: string): Html =>
		h.span(
			[h.Class("whitespace-pre-wrap break-words")],
			parseMarkdownLinks(sanitize(raw)).flatMap((part) =>
				part.type === "link"
					? [link(part.href, part.value)]
					: textWithUrls(part.value),
			),
		);
	const logBlock = (title: string, value: unknown) =>
		h.div(
			[h.Class("mb-2")],
			[
				h.div([h.Class("font-semibold text-base-content/70")], [title]),
				h.pre(
					[h.Class("mt-1 whitespace-pre-wrap break-words")],
					[formatLog(value)],
				),
			],
		);
	const entry = (item: Entry): Html =>
		Entry.match<Html>(item, {
			Tool: ({ id, toolName, input, output, expanded }) => {
				const hasLog = Option.isSome(input) || Option.isSome(output);
				return h.div(
					[
						h.Class("flex items-start gap-3"),
						h.DataAttribute("testid", "tool-call"),
					],
					[
						avatar(
							"bg-secondary text-secondary-content",
							Icons.Workflow(h, { size: 18 }),
						),
						h.div(
							[h.Class("flex max-w-[75%] flex-1 flex-col space-y-2")],
							[
								h.div(
									[
										h.Class(
											"flex items-center gap-2 text-xs font-semibold text-base-content/60",
										),
									],
									[Icons.Workflow(h, { size: 14 }), h.span([], ["Tool call"])],
								),
								h.div(
									[
										h.Class(
											"relative overflow-hidden rounded-xl border-2 border-orange-300/70 bg-orange-50 p-3 shadow-sm",
										),
									],
									[
										h.div(
											[
												h.AriaHidden(true),
												h.Class(
													"pointer-events-none absolute inset-0 opacity-50",
												),
												h.Style({
													backgroundImage:
														"radial-gradient(circle at 8px 8px, color-mix(in oklab, var(--color-secondary) 55%, transparent) 1.25px, transparent 0)",
													backgroundSize: "14px 14px",
													backgroundBlendMode: "multiply",
												}),
											],
											[],
										),
										h.div(
											[
												h.Class(
													"relative flex items-center gap-2 text-sm text-base-content",
												),
											],
											[
												Icons.Bot(h, { size: 16, class: "text-orange-600" }),
												h.div(
													[h.Class("flex flex-col")],
													[h.span([h.Class("font-semibold")], [toolName])],
												),
											],
										),
										...(hasLog
											? [
													h.div(
														[h.Class("relative mt-3 flex flex-col gap-2")],
														[
															h.button(
																[
																	h.Type("button"),
																	h.Class("btn btn-xs btn-outline w-fit"),
																	h.AriaLabel(
																		expanded ? "Hide log" : "View log",
																	),
																	h.OnClick(Message.ToggledToolLog({ id })),
																],
																[expanded ? "Hide log" : "View log"],
															),
															...(expanded
																? [
																		h.div(
																			[
																				h.Class(
																					"rounded-lg border border-orange-200 bg-base-100 p-2 text-xs font-mono text-base-content",
																				),
																			],
																			[
																				...Option.match(input, {
																					onNone: () => [],
																					onSome: (value) => [
																						logBlock("Input", value),
																					],
																				}),
																				...Option.match(output, {
																					onNone: () => [],
																					onSome: (value) => [
																						logBlock("Output", value),
																					],
																				}),
																			],
																		),
																	]
																: []),
														],
													),
												]
											: []),
									],
								),
							],
						),
					],
				);
			},
			RecordLinks: ({ items }) =>
				h.div(
					[h.Class("flex items-start gap-3")],
					[
						avatar("bg-primary/10 text-primary", Icons.Link2(h, { size: 18 })),
						h.div(
							[h.Class("flex max-w-[85%] flex-1 flex-col space-y-2")],
							[
								h.div(
									[
										h.Class(
											"flex items-center gap-2 text-xs font-semibold text-base-content/60",
										),
									],
									[Icons.Link2(h, { size: 14 }), h.span([], ["Found"])],
								),
								h.div(
									[h.Class("flex flex-wrap gap-2")],
									items.map((link) =>
										h.a(
											[
												h.Href(
													linkHref(
														shared.route,
														link.type === "node"
															? Route.Record({ id: link.id, asOf: shared.asOf })
															: Route.Entry({ id: link.id, asOf: shared.asOf }),
													),
												),
												h.Class(
													"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-base-300 bg-base-100 hover:bg-base-200 hover:border-primary/50 transition-colors text-sm font-medium text-base-content shadow-sm",
												),
											],
											[
												link.type === "node"
													? Icons.User(h, { size: 14, class: "text-primary" })
													: Icons.StickyNote(h, {
															size: 14,
															class: "text-secondary",
														}),
												h.span(
													[h.Class("max-w-[200px] truncate")],
													[link.title],
												),
											],
										),
									),
								),
							],
						),
					],
				),
			Text: ({ content: text, sender }) => {
				const isUser = sender === "user";
				return h.div(
					[
						h.Class(
							`flex items-start gap-3${isUser ? " flex-row-reverse" : ""}`,
						),
					],
					[
						avatar(
							isUser
								? "bg-primary text-primary-content"
								: "bg-secondary text-secondary-content",
							h.span([h.Class("text-sm font-semibold")], [isUser ? "Y" : "A"]),
						),
						h.div(
							[
								h.Class(
									`flex max-w-[75%] flex-1 flex-col space-y-1${isUser ? " items-end text-right" : ""}`,
								),
							],
							[
								h.div(
									[h.Class("text-xs font-semibold text-base-content/60")],
									[isUser ? "You" : "Assistant"],
								),
								h.div(
									[
										h.Class(
											"bg-base-100 border rounded-xl shadow-sm p-3 w-full text-base-content text-sm space-y-2",
										),
									],
									[content(text)],
								),
							],
						),
					],
				);
			},
		});
	return h.div(
		[h.Class("mx-auto flex h-full w-full max-w-3xl flex-col")],
		[
			// column-reverse keeps the newest entry in view as the transcript grows (v1 scrolled on each change).
			h.div(
				[
					h.Class(
						"flex-1 overflow-y-auto space-y-4 px-4 py-6 flex flex-col-reverse",
					),
				],
				model.entries.length === 0
					? [
							h.div(
								[
									h.Class(
										"flex items-center justify-center h-full text-base-content/50",
									),
								],
								["Start a conversation with AI"],
							),
						]
					: [h.div([h.Class("space-y-4")], model.entries.map(entry))],
			),
			h.form(
				[
					h.Class("border-t border-base-300 p-4"),
					h.OnSubmit(Message.Submitted()),
				],
				[
					h.div(
						[
							h.Class(
								"card rounded-2xl p-3 bg-base-100 border border-base-200",
							),
						],
						[
							h.div(
								[h.Class("flex flex-col")],
								[
									h.textarea([
										h.Id(INPUT_ID),
										h.AriaLabel("Message"),
										h.Value(model.input),
										h.Placeholder("Type your message here..."),
										// `field-sizing: content` grows the textarea with its content (v1 resized it in an effect).
										h.Class(
											"textarea w-full bg-transparent border-none focus:outline-none resize-none overflow-hidden min-h-16 [field-sizing:content]",
										),
										h.Disabled(pending),
										h.Autofocus(true),
										h.Rows(1),
										h.OnInput((value) => Message.ChangedInput({ value })),
										h.OnKeyDownPreventDefault((key, { shiftKey }) =>
											key === "Enter" && !shiftKey
												? Option.some(Message.Submitted())
												: Option.none(),
										),
									]),
									h.div(
										[h.Class("flex items-center gap-2 justify-between")],
										[
											h.div(
												[h.Class("flex items-center gap-2")],
												[
													h.button(
														[
															h.Type("button"),
															h.Class("btn btn-circle btn-ghost btn-disabled"),
															h.Title("File uploads coming soon"),
															h.AriaLabel("File uploads coming soon"),
															h.Disabled(true),
														],
														[Icons.Paperclip(h, { size: 18 })],
													),
													h.button(
														[
															h.Type("button"),
															h.Class("btn btn-circle btn-ghost btn-disabled"),
															h.Title("Image uploads coming soon"),
															h.AriaLabel("Image uploads coming soon"),
															h.Disabled(true),
														],
														[Icons.Image(h, { size: 18 })],
													),
												],
											),
											h.button(
												[
													h.Type("submit"),
													h.Class(
														"btn btn-circle btn-accent text-accent-content",
													),
													h.AriaLabel("Send message"),
													...busyAttributes(
														h,
														pending,
														model.input.trim() === "",
													),
												],
												[
													pending
														? busySpinner(h, pending)
														: Icons.Send(h, { size: 18 }),
												],
											),
										],
									),
								],
							),
						],
					),
				],
			),
		],
	);
});
